/**
 * Szám- és dátumformázás `Intl`-en, nem beégetve.
 *
 * A makettek `1 234 567 Ft` és `2026-08-18` alakot használnak — ez MAGYAR
 * konvenció, nem univerzális. Ha három helyen égetjük be, három helyen romlik
 * el. (`m0b-build-terv.md` §8 — „Az i18n-váz — miért az 1. lépésben".)
 *
 * Ez a fájl a `packages/i18n` kanonikus forrásában él, és onnan másolódik
 * a szervizekbe (`python3 scripts/shared-sync.py`). Itt szerkeszd, ne a másolatot.
 */

export const DEFAULT_LOCALE = 'hu-HU';
export const DEFAULT_CURRENCY = 'HUF';
export const DEFAULT_TIME_ZONE = 'Europe/Budapest';

export interface FormatOptions {
  locale?: string;
  currency?: string;
  timeZone?: string;
}

/**
 * A NULLA TIZEDESJEGYŰ PÉNZNEMEK — a gyakorlat szerint, nem az ISO szerint.
 *
 * ⚠️ AZ `Intl` A FORINTRA 2 JEGYET AD, és ez itt HIBÁS kimenet. Az ISO-4217
 * a HUF-ot 2 tizedessel tartja nyilván (a fillér formálisan létezik), a
 * magyar számlagyakorlat viszont egész forintot ír — 1999 óta nincs fillér
 * forgalomban. Rábízva az `Intl`-re `1 573 800,00 Ft` lenne, ami egyetlen
 * magyar bizonylaton sem így áll. (Mérve: a csere pontosan ezt okozta.)
 *
 * Ami NEM kerül ide: az EUR, mert ott a két jegy VALÓDI — a 141,24 EUR
 * fillérje az összeg része.
 */
const NULLA_TIZEDESU_PENZNEMEK = new Set(['HUF']);

/**
 * Összeg: ezres szóközzel tagolva, a pénznem jele a szám UTÁN.
 * `1234567` → `1 234 567 Ft` · `141.24` + `EUR` → `141,24 EUR`
 *
 * ⚠️ A TIZEDESJEGY A PÉNZNEMÉ, NEM A FORMÁZÓÉ. (#40)
 *
 * Korábban itt fix `maximumFractionDigits: 0` állt, azzal az indoklással,
 * hogy „a forintnak nincs váltópénze a gyakorlatban". A MONDAT IGAZ — de a
 * FORINTRÓL szól, nem minden pénznemről. Egy EUR-számlán ez levágta a
 * filléreket: a 141,24 EUR-ból `141` lett, és mivel a hívók a `currency`-t
 * sem adták át, a végeredmény `141 Ft` — rossz összeg, rossz pénznemben,
 * egy VALÓBAN JÓL OLVASOTT bizonylaton (mérve 2026-09-14, #26).
 *
 * A szabály tehát nem az, hogy „az `Intl` döntsön" — az a forintot rontaná
 * el —, hanem hogy a NULLA JEGY a forint sajátja, és csak őrá álljon.
 */
export function formatAmount(value: number, options: FormatOptions = {}): string {
  const currency = isoCurrencyOr(options.currency, DEFAULT_CURRENCY);
  const egeszPenznem = NULLA_TIZEDESU_PENZNEMEK.has(currency);

  return new Intl.NumberFormat(options.locale ?? DEFAULT_LOCALE, {
    style: 'currency',
    currency,
    ...(egeszPenznem ? { maximumFractionDigits: 0, minimumFractionDigits: 0 } : {}),
  }).format(value);
}

/**
 * ⚠️ A PÉNZNEM-KÓD SZŰRÉSE — KÜLÖNBEN EGY OCR-OLVASAT VISZI A KÉPERNYŐT.
 *
 * Az `Intl.NumberFormat` `RangeError`-t DOB a nem ISO-4217 alakú kódra
 * (`new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'Ft ' })`
 * → `Invalid currency code`), és a könyvelői adminban NINCS `ErrorBoundary`:
 * egy dobás a teljes főképernyőt üresre viszi.
 *
 * ÉS A ROSSZ ALAK NEM ELMÉLETI. A `Document.currency` értékét a mezőkinyerés
 * írja, a NYERS OCR-olvasatból — az `ocr-field.extraction.ts`
 * `FORINT_JELOLESEK`-je maga sorolja fel, hogy a papíron `FT`, `FT.` és
 * `FORINT` is előfordul. Az oszlop `@db.Char(3)`, tehát a `Ft` `'Ft '`-ként
 * jön vissza, szóközzel — pontosan az az alak, ami dob.
 *
 * A visszaesés a forintra CSENDES, és ez itt a helyes: a formázó nem az a
 * hely, ahol egy rossz OCR-olvasatot jelenteni kell. A jelentés a kinyerés
 * bizonyosság-mérésének a dolga.
 */
function isoCurrencyOr(currency: string | undefined, fallback: string): string {
  const jel = currency?.trim().toUpperCase();
  return jel !== undefined && /^[A-Z]{3}$/.test(jel) ? jel : fallback;
}

/**
 * Tagolt szám mértékegység nélkül: `1234567` → `1 234 567`
 */
export function formatNumber(value: number, options: FormatOptions = {}): string {
  return new Intl.NumberFormat(options.locale ?? DEFAULT_LOCALE).format(value);
}

/**
 * Dátum ISO-alakban, a magyar konvenció szerint: `2026-07-28`.
 *
 * Szándékosan `en-CA`-val formázunk: az `Intl` ezzel ad `YYYY-MM-DD`-t
 * időzóna-helyesen. A `toISOString()` UTC-re vált, és egy budapesti éjfél
 * előtti időbélyeget az ELŐZŐ napra írna — csendes, egynapos elcsúszás.
 */
export function formatDate(value: Date | string, options: FormatOptions = {}): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: options.timeZone ?? DEFAULT_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/**
 * Hónap és nap: `2026-07-28` → `07-28`.
 *
 * A KUPACBAN AZ ÉV ZAJ. A munkalista 272px-es hasábján minden sor az idei
 * évből való, és a négy jegy a partner nevétől venné el a helyet. A teljes
 * dátum ott áll, ahol számít — a bizonylat fejlécén és a mezők közt.
 *
 * A `formatDate`-re épül, tehát ugyanazt az időzóna-helyes napot vágja,
 * amit az kiír; egy külön `Intl`-hívás elcsúszhatna tőle.
 *
 * CSAK AZ IDŐZÓNÁT VESZI ÁT, és ez nem szűkmarkúság: az `5`-ös vágás a
 * `YYYY-MM-DD` alakra épül, amit a `formatDate` beégetett `en-CA`-ja ad. Egy
 * `locale`-t is fogadó aláírás azt ígérné, hogy a nyelv számít — és ha valaki
 * később tényleg rákötné, ez a függvény némán szemetet adna vissza
 * (`"7/28/2026".slice(5)` → `"026"`), típushiba nélkül.
 */
export function formatMonthDay(
  value: Date | string,
  options: Pick<FormatOptions, 'timeZone'> = {}
): string {
  return formatDate(value, options).slice(5);
}

/**
 * Dátum és perc: `2026-07-28 14:32`
 */
export function formatDateTime(value: Date | string, options: FormatOptions = {}): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  const time = new Intl.DateTimeFormat('hu-HU', {
    timeZone: options.timeZone ?? DEFAULT_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
  return `${formatDate(date, options)} ${time}`;
}

/**
 * Adószám tagolva: `12345678206` → `12345678-2-06`.
 * A már tagolt bemenetet változatlanul adja vissza; a nem 11 jegyűt is —
 * a formázó nem validál, a validálás nem itt lakik.
 *
 * ⚠️ A BETŰS ALAKHOZ NEM NYÚL (#79). A partner mezőjében közösségi adószám
 * is állhat: a `HR41745473821` számjegyei 11 jegyűek, és a tagolás
 * `41745473-8-21`-et csinálna belőle — egy magyar adószámot, országkód nélkül.
 */
export function formatTaxNumber(taxNumber: string): string {
  const digits = taxNumber.replace(/\D/g, '');
  if (digits.length !== 11 || /[A-Za-z]/.test(taxNumber)) {
    return taxNumber;
  }
  return `${digits.slice(0, 8)}-${digits.slice(8, 9)}-${digits.slice(9, 11)}`;
}
